import { useContext, useEffect, useRef, useState, type FC } from 'react';
import classNames from 'classnames';
import useScrollInfo from 'react-element-scroll-hook';
import { useTranslation } from 'react-i18next';

import { getTournamentBracketFetchUrl } from 'app/utils/entities/tournament';
import LoadingContainer from 'app/components/LoadingContainer';
import ConfigContext from 'app/common/ConfigContext';
import { getDisplayedLeagueName } from 'app/config/customerSpecific.config.helper';
import BlockTemplate from 'app/components/Blocks/BlockTemplate';
import Bracket from 'app/components/Blocks/BracketsBlock/Bracket';
import { getFirstSelectedTournament, orderSeriesTournaments } from 'app/utils/entities/series_data';
import SingleArrowIcon from 'app/icons/arrow-min-right.svg';
import CircleLoader from 'app/components/CircleLoader/CircleLoader';
import ScrollIntoView from 'app/components/ScrollIntoView';
import { usePersistentFetch } from 'app/hooks/usePersistentFetch';
import { ERROR_UNKNOWN } from 'app/constants/errors';

import style from './style.module.scss';

interface Props {
  data: TournamentsBlockSeries;
}

// Allow to init useScrollInfo with a fake div element, before initializing it with the real which is not accessible in JSX
const FakeDivElement = document.createElement('div');
let flag = true;

const TournamentsBlock: FC<Props> = ({ data }) => {
  const { t } = useTranslation();
  const configContext = useContext(ConfigContext);
  const [selectedTournamentId, setSelectedTournamentId] = useState<number | null>(
    getFirstSelectedTournament(data.tournaments)?.id ?? null
  );

  const baseRef = useRef<HTMLDivElement>(null);

  // I'd like to rewrite this plugin but for now it'll do
  const [scrollInfo, setRef] = useScrollInfo();

  // Use useFetch instead of useRequest to be able to use the cache-first fetch policy
  const {
    status,
    data: displayedTournament,
    error
  } = usePersistentFetch<TournamentBracket>({
    method: 'GET',
    fetchPolicy: 'cache-first',
    url: getTournamentBracketFetchUrl(selectedTournamentId!, configContext),
    preventRequest: !selectedTournamentId
  });

  if (flag) {
    setRef(FakeDivElement);
    flag = false;
  }

  useEffect(() => {
    setRef(baseRef.current);
  }, [baseRef]);

  useEffect(
    () => () => {
      flag = true;
    },
    []
  );

  const scrollInfoX = scrollInfo.x.percentage;

  const orderedTournaments = orderSeriesTournaments(t, data.tournaments);

  return (
    <BlockTemplate
      title={[
        getDisplayedLeagueName(data.league.name, data.league.id, configContext.customer),
        data.name,
        data.year
      ]
        .filter(Boolean)
        .join(' ')}
      hasData={orderedTournaments.length > 0}
    >
      <div className={style.tabsContainer}>
        <div
          className={classNames(style.leftArrow, {
            [style.arrowDisplayed]: scrollInfoX !== null && scrollInfoX !== 0
          })}
        >
          <SingleArrowIcon className={style.arrowIcon} />
        </div>
        <div className={style.tabs} ref={baseRef}>
          {orderedTournaments.map(tournament => (
            <ScrollIntoView
              key={tournament.id}
              shouldScroll={selectedTournamentId === tournament.id}
            >
              <button
                onClick={() => setSelectedTournamentId(tournament.id)}
                type="button"
                className={classNames(style.tournamentTab, {
                  [style.selected]: selectedTournamentId === tournament.id
                })}
              >
                {tournament.status === 'running' && <span className={style.liveIndicator} />}
                <span className={style.tabName} title={tournament.name}>
                  {tournament.name}
                </span>
              </button>
            </ScrollIntoView>
          ))}
        </div>
        <div
          className={classNames(style.rightArrow, {
            [style.arrowDisplayed]: scrollInfoX !== null && scrollInfoX !== 1
          })}
        >
          <SingleArrowIcon className={style.arrowIcon} />
        </div>
      </div>
      <LoadingContainer
        data={displayedTournament}
        error={error ? ERROR_UNKNOWN : null}
        status={status}
        firstLoadIndicatorOnly
        loader={<CircleLoader size={20} className={style.loader} />}
        id={`tournaments_block_${displayedTournament?.id}`}
      >
        <div className={style.bracketContainer}>
          <Bracket bracket={displayedTournament!} withTabs />
        </div>
      </LoadingContainer>
    </BlockTemplate>
  );
};

TournamentsBlock.displayName = 'TournamentsBlock';

export default TournamentsBlock;
